// src/pages/ClientDetails.js - VERSION AVEC GESTION DES ERREURS D'ABONNEMENT
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);

  // Couleurs de la charte graphique
  const COLORS = {
    primary: '#0B3C5D',     // Bleu Sécurité
    secondary: '#1E88C8',   // Bleu Tech
    accent: '#4FC3F7',      // Bleu Clair
    darkText: '#2E2E2E',    // Gris Foncé
    lightBg: '#F5F7FA',     // Gris Clair
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  // Fonction pour obtenir les styles du contrat selon l'état
  const getContratStyle = (etat) => {
    switch (etat) {
      case "vert": return { 
        backgroundColor: '#e8f5e9', 
        color: '#2e7d32',
        borderColor: '#4caf50'
      };
      case "orange": return { 
        backgroundColor: '#fff3e0', 
        color: '#ef6c00',
        borderColor: '#ff9800'
      };
      case "rouge": return { 
        backgroundColor: '#ffebee', 
        color: '#c62828',
        borderColor: '#f44336'
      };
      case "expire": return { 
        backgroundColor: '#f5f5f5', 
        color: '#757575',
        borderColor: '#9e9e9e'
      };
      default: return { 
        backgroundColor: '#f5f5f5', 
        color: '#757575',
        borderColor: '#9e9e9e'
      };
    }
  };

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Charger les détails du client - version avec useCallback
  const fetchClientDetails = useCallback(async () => {
    if (!userInfo || !id) return;
    
    setLoading(true);
    setError("");
    
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Session expirée");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/clients/get.php?id=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setClient(response.data.client);
        setSites(response.data.sites || []);
      } else {
        setError(response.data?.message || "Client non trouvé");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Accès interdit. Vous n'avez pas les permissions nécessaires.");
      } else if (err.response?.status === 404) {
        setError("Client non trouvé.");
        setTimeout(() => navigate("/clients"), 3000);
      } else if (!err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(`Erreur ${err.response.status}: ${err.response.data?.message || "Erreur technique"}`);
      }
    } finally {
      setLoading(false);
    }
  }, [id, userInfo, navigate]);

  useEffect(() => {
    fetchClientDetails();
  }, [fetchClientDetails]);

  // Vérifier si l'utilisateur est gestionnaire
  const isGestionnaire = userInfo && String(userInfo.profile).trim() === "3";
  const isDeleted = client?.date_supprimer_client && client.date_supprimer_client !== "0000-00-00 00:00:00";

  // Fonction pour changer le statut du client
  const handleChangeStatus = async (newStatus) => {
    if (!client || !window.confirm(`Êtes-vous sûr de vouloir ${newStatus === 1 ? 'activer' : 'désactiver'} ce client ?`)) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/clients/change_status.php`,
        { id_client: client.id_client, statut_client: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg(`Client ${newStatus === 1 ? 'activé' : 'désactivé'} avec succès.`);
        // Recharger les données
        fetchClientDetails();
      } else {
        setError(response.data?.message || "Erreur lors du changement de statut");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      // 🔍 Vérifier si c'est une erreur d'abonnement
      if (err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        // Message avec bouton de souscription
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
      } else {
        setError(err.response?.data?.message || "Erreur technique");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour supprimer le client
  const handleDelete = async () => {
    if (!client || !window.confirm(`Êtes-vous sûr de vouloir supprimer le client ${client.nom_client} ${client.prenom_client} ? Cette action est irréversible.`)) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/clients/delete.php`,
        { id_client: client.id_client },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Client supprimé avec succès.");
        setTimeout(() => navigate("/clients"), 2000);
      } else {
        setError(response.data?.message || "Erreur lors de la suppression");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      // 🔍 Vérifier si c'est une erreur d'abonnement
      if (err.response?.data?.code === 'ABONNEMENT_REQUIS') {
        // Message avec bouton de souscription
        setError(
          <div>
            <div style={{ whiteSpace: 'pre-line', marginBottom: '15px' }}>
              {err.response.data.message}
            </div>
            <div className="d-flex gap-2 justify-content-center">
              <a 
                href="/abonnements/create"
                className="btn fw-semibold"
                style={{
                  backgroundColor: '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#45a049'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#4caf50'}
              >
                <i className="bi bi-box-arrow-up-right"></i>
                Souscrire à un abonnement
              </a>
              <button
                className="btn fw-semibold"
                onClick={() => setError("")}
                style={{
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 20px'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#d32f2f'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#f44336'}
              >
                Fermer
              </button>
            </div>
          </div>
        );
      } else {
        setError(err.response?.data?.message || "Erreur technique");
      }
    } finally {
      setActionLoading(false);
    }
  };

  // Fonction pour restaurer le client
  const handleRestore = async () => {
    if (!client || !window.confirm(`Êtes-vous sûr de vouloir restaurer le client ${client.nom_client} ${client.prenom_client} ?`)) {
      return;
    }
    
    setActionLoading(true);
    setSuccessMsg("");
    setError("");
    
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API_BASE_URL}/clients/restore.php`,
        { id_client: client.id_client },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (response.data?.success) {
        setSuccessMsg("Client restauré avec succès.");
        fetchClientDetails();
      } else {
        setError(response.data?.message || "Erreur lors de la restauration");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setError(err.response?.data?.message || "Erreur technique");
    } finally {
      setActionLoading(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00") {
      return "-";
    }
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Composant Tooltip simple
  const Tooltip = ({ children, text, position = "top" }) => (
    <div className="tooltip-container position-relative d-inline-block">
      {children}
      <div className={`tooltip-text position-absolute bg-dark text-white p-2 rounded shadow-sm`}
           style={{
             bottom: position === "top" ? "100%" : "auto",
             top: position === "bottom" ? "100%" : "auto",
             left: "50%",
             transform: "translateX(-50%)",
             whiteSpace: "nowrap",
             zIndex: 1000,
             fontSize: "0.875rem",
             marginBottom: position === "top" ? "5px" : "0",
             marginTop: position === "bottom" ? "5px" : "0",
           }}>
        {text}
        <div className="tooltip-arrow position-absolute"
             style={{
               left: "50%",
               transform: "translateX(-50%)",
               [position === "top" ? "top" : "bottom"]: "100%",
               borderWidth: "5px",
               borderStyle: "solid",
               borderColor: position === "top" 
                 ? "#212529 transparent transparent transparent"
                 : "transparent transparent #212529 transparent"
             }}></div>
      </div>
    </div>
  );

  if (loading || !userInfo) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du client</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/clients">Clients</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}></div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des détails...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !client) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du client</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/clients">Clients</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.danger }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body">
              <div className="alert alert-danger alert-dismissible fade show" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: '4px solid ' + COLORS.danger
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error || "Client non trouvé"}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
              <div className="text-center mt-3">
                <button 
                  className="btn fw-semibold"
                  onClick={() => navigate("/clients")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour à la liste
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Styles pour le badge de contrat
  const contratStyle = getContratStyle(client.statistiques?.etat_contrat || "inconnu");

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails du client</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/clients">Clients</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>
              {client.nom_client} {client.prenom_client}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {/* Messages */}
        {successMsg && (
          <div className="alert alert-success alert-dismissible fade show mb-3" style={{ 
            backgroundColor: '#e8f5e9',
            borderColor: COLORS.success,
            color: '#2e7d32',
            borderRadius: '8px',
            borderLeft: '4px solid ' + COLORS.success,
            whiteSpace: "pre-line"
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
              <div className="flex-grow-1">{successMsg}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setSuccessMsg("")}
              ></button>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-3" style={{ 
            backgroundColor: '#ffebee',
            borderColor: COLORS.danger,
            color: '#c62828',
            borderRadius: '8px',
            borderLeft: '4px solid ' + COLORS.danger,
            whiteSpace: "pre-line"
          }}>
            <div className="d-flex align-items-start">
              <i className="bi bi-exclamation-triangle me-2 mt-1"></i>
              <div className="flex-grow-1">{error}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError("")}
              ></button>
            </div>
          </div>
        )}

        <div className="row">
          {/* Colonne gauche: Profil + Actions */}
          <div className="col-lg-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              height: '100%'
            }}>
              <div className="card-body">
                <div className="text-center mb-4">
                  {/* Avatar */}
                  <div className="mb-3">
                    <div 
                      className="rounded-circle d-inline-flex align-items-center justify-content-center text-white mx-auto"
                      style={{ 
                        width: "100px", 
                        height: "100px",
                        backgroundColor: COLORS.primary,
                        fontSize: '2rem',
                        boxShadow: '0 4px 12px rgba(11, 60, 93, 0.3)'
                      }}
                    >
                      <span style={{ fontWeight: '600' }}>
                        {client.nom_client?.charAt(0) || ''}{client.prenom_client?.charAt(0) || ''}
                      </span>
                    </div>
                  </div>
                  
                  <h4 style={{ color: COLORS.primary, fontWeight: '600' }}>{client.nom_client} {client.prenom_client}</h4>
                  <p className="mb-2" style={{ color: COLORS.darkText }}></p>
                  
                  <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
                    <span className="badge" style={{ 
                      backgroundColor: client.statut_client === 1 ? '#e8f5e9' : 
                                     client.statut_client === 0 ? '#f5f5f5' : '#fff8e1',
                      color: client.statut_client === 1 ? '#2e7d32' : 
                            client.statut_client === 0 ? '#757575' : '#ff8f00',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontWeight: '500'
                    }}>
                      {client.statut_client_label}
                    </span>
                    <span className="badge" style={{ 
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary,
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontWeight: '500'
                    }}>
                      {client.type_client_label}
                    </span>
                    {isDeleted && (
                      <span className="badge" style={{ 
                        backgroundColor: '#ffebee',
                        color: '#c62828',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontWeight: '500'
                      }}>
                        Supprimé
                      </span>
                    )}
                  </div>
                </div>

                {/* SECTION ACTIONS */}
                <div className="border-top pt-4 mt-3" style={{ borderColor: COLORS.lightBg }}>
                  <h6 className="mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-lightning-charge"></i>
                    </div>
                    Actions rapides
                  </h6>
                  
                  <div className="d-flex flex-wrap gap-3 justify-content-center align-items-center mb-3">
                    {/* Bouton MODIFIER */}
                    {isGestionnaire && !isDeleted && (
                      <Tooltip text="Modifier le client">
                        <button 
                          className="btn fw-semibold"
                          onClick={() => navigate(`/clients/edit/${client.id_client}`)}
                          disabled={actionLoading}
                          style={{
                            color: 'white',
                            backgroundColor: COLORS.secondary,
                            borderColor: COLORS.secondary,
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!actionLoading) {
                              e.target.style.backgroundColor = COLORS.primary;
                              e.target.style.borderColor = COLORS.primary;
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(30, 136, 200, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = COLORS.secondary;
                            e.target.style.borderColor = COLORS.secondary;
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {actionLoading ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-pencil-fill fs-5"></i>
                          )}
                        </button>
                      </Tooltip>
                    )}
                    
                    {/* Bouton ACTIVER/DÉSACTIVER */}
                    {isGestionnaire && !isDeleted && (
                      <Tooltip text={client.statut_client === 1 ? "Désactiver le client" : "Activer le client"}>
                        <button 
                          className="btn fw-semibold"
                          onClick={() => handleChangeStatus(client.statut_client === 1 ? 0 : 1)}
                          disabled={actionLoading}
                          style={{
                            color: 'white',
                            backgroundColor: client.statut_client === 1 ? COLORS.warning : COLORS.success,
                            borderColor: client.statut_client === 1 ? COLORS.warning : COLORS.success,
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!actionLoading) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {actionLoading ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className={`bi fs-5 ${client.statut_client === 1 ? 'bi-person-x-fill' : 'bi-person-check-fill'}`}></i>
                          )}
                        </button>
                      </Tooltip>
                    )}
                    
                    {/* Bouton SUPPRIMER */}
                    {isGestionnaire && !isDeleted && (
                      <Tooltip text="Supprimer le client">
                        <button 
                          className="btn fw-semibold"
                          onClick={handleDelete}
                          disabled={actionLoading}
                          style={{
                            color: 'white',
                            backgroundColor: COLORS.danger,
                            borderColor: COLORS.danger,
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!actionLoading) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(244, 67, 54, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {actionLoading ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-trash-fill fs-5"></i>
                          )}
                        </button>
                      </Tooltip>
                    )}
                    
                    {/* Bouton RESTAURER */}
                    {isGestionnaire && isDeleted && (
                      <Tooltip text="Restaurer le client">
                        <button 
                          className="btn fw-semibold"
                          onClick={handleRestore}
                          disabled={actionLoading}
                          style={{
                            color: 'white',
                            backgroundColor: COLORS.success,
                            borderColor: COLORS.success,
                            width: "48px",
                            height: "48px",
                            borderRadius: "12px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            if (!actionLoading) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 4px 8px rgba(76, 175, 80, 0.3)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                          }}
                        >
                          {actionLoading ? (
                            <span className="spinner-border spinner-border-sm"></span>
                          ) : (
                            <i className="bi bi-arrow-clockwise fs-5"></i>
                          )}
                        </button>
                      </Tooltip>
                    )}
                  </div>

                  {/* Info si pas d'actions */}
                  {!isGestionnaire && (
                    <div className="alert mt-3 p-3 text-center" style={{ 
                      backgroundColor: '#E3F2FD',
                      borderColor: COLORS.accent,
                      color: COLORS.primary,
                      borderRadius: '8px'
                    }}>
                      <small>
                        <i className="bi bi-info-circle me-1"></i>
                        Seuls les gestionnaires peuvent modifier les clients.
                      </small>
                    </div>
                  )}
                </div>
                
                {/* Statistiques */}
                <div className="border-top pt-4 mt-3" style={{ borderColor: COLORS.lightBg }}>
                  <h6 className="mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-2" style={{ 
                      width: '30px',
                      height: '30px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-bar-chart"></i>
                    </div>
                    Statistiques
                  </h6>
                  <div className="row text-center">
                    <div className="col-6 mb-3">
                      <div className="card" style={{ 
                        backgroundColor: 'transparent',
                        border: `2px solid ${COLORS.primary}`,
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }}>
                        <div className="card-body p-2">
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-buildings"></i>
                          </div>
                          <h5 className="mt-2 mb-1" style={{ color: COLORS.primary, fontWeight: '600' }}>{client.statistiques?.nb_sites || 0}</h5>
                          <p className="text-muted mb-0 small">Sites</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="col-6 mb-3">
                      <div className="card" style={{ 
                        backgroundColor: 'transparent',
                        border: `2px solid ${COLORS.success}`,
                        borderRadius: '8px',
                        boxShadow: 'none'
                      }}>
                        <div className="card-body p-2">
                          <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32'
                          }}>
                            <i className="bi bi-people"></i>
                          </div>
                          <h5 className="mt-2 mb-1" style={{ color: '#2e7d32', fontWeight: '600' }}>{client.statistiques?.nb_agents_affectes || 0}</h5>
                          <p className="text-muted mb-0 small">Agents</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bouton RETOUR */}
                <div className="d-grid mt-4">
                  <button 
                    className="btn fw-semibold"
                    onClick={() => navigate("/clients")}
                    disabled={actionLoading}
                    style={{
                      color: COLORS.darkText,
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '12px 24px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!actionLoading) {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour à la liste
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne droite: Informations détaillées */}
          <div className="col-lg-8">
            {/* Informations générales */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-info-circle me-2" style={{ color: COLORS.secondary }}></i>
                  Informations générales
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%" style={{ fontWeight: '500', color: COLORS.darkText }}>Nom:</td>
                            <td style={{ color: COLORS.darkText }}>{client.nom_client}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500', color: COLORS.darkText }}>Prénom:</td>
                            <td style={{ color: COLORS.darkText }}>{client.prenom_client || "-"}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500', color: COLORS.darkText }}>Téléphone:</td>
                            <td style={{ color: COLORS.darkText }}>{client.tel_client || "-"}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500', color: COLORS.darkText }}>Email:</td>
                            <td style={{ color: COLORS.darkText }}>{client.email_client || "-"}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500', color: COLORS.darkText }}>Société:</td>
                            <td style={{ color: COLORS.darkText }}>{client.nom_societe || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <table className="table table-sm table-borderless">
                        <tbody>
                          <tr>
                            <td width="40%" style={{ fontWeight: '500', color: COLORS.darkText }}>Crée le:</td>
                            <td style={{ color: COLORS.darkText }}>{formatDate(client.created_at)}</td>
                          </tr>
                          <tr>
                            <td style={{ fontWeight: '500', color: COLORS.darkText }}>Modifié le:</td>
                            <td style={{ color: COLORS.darkText }}>{formatDate(client.updated_at) || "-"}</td>
                          </tr>
                          {isDeleted && (
                            <tr>
                              <td style={{ fontWeight: '500', color: COLORS.darkText }}>Supprimé le:</td>
                              <td style={{ color: COLORS.danger }}>{formatDate(client.date_supprimer_client)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Informations légales */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-file-text me-2" style={{ color: COLORS.secondary }}></i>
                  Informations légales
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-credit-card"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: COLORS.darkText }}>Pièce d'identité</div>
                          <div style={{ color: COLORS.darkText }}>{client.piece_identite_client || "-"}</div>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center mb-3">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-file-earmark-text"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: COLORS.darkText }}>NIF</div>
                          <div style={{ color: COLORS.darkText }}>{client.nif_client || "-"}</div>
                        </div>
                      </div>
                      
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '40px',
                          height: '40px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-file-earmark-text"></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: '500', color: COLORS.darkText }}>RCCM</div>
                          <div style={{ color: COLORS.darkText }}>{client.rccm_client || "-"}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contrat - SECTION CORRIGÉE */}
            <div className="card mb-4" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-calendar-check me-2" style={{ color: COLORS.secondary }}></i>
                  Contrat
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="card" style={{ 
                      backgroundColor: '#E3F2FD',
                      border: 'none',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: COLORS.primary,
                            color: 'white'
                          }}>
                            <i className="bi bi-calendar-plus"></i>
                          </div>
                          <div>
                            <h6 className="mb-1" style={{ color: COLORS.darkText }}>Début du contrat</h6>
                            <p className="mb-0" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              {formatDate(client.date_debut_contrat_client)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card" style={{ 
                      backgroundColor: '#E3F2FD',
                      border: 'none',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body">
                        <div className="d-flex align-items-center mb-3">
                          <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                            width: '40px',
                            height: '40px',
                            backgroundColor: contratStyle.backgroundColor,
                            color: contratStyle.color,
                            border: `2px solid ${contratStyle.borderColor}`
                          }}>
                            <i className="bi bi-calendar-x"></i>
                          </div>
                          <div>
                            <h6 className="mb-1" style={{ color: COLORS.darkText }}>Fin du contrat</h6>
                            <p className="mb-0" style={{ color: COLORS.darkText, fontWeight: '500' }}>
                              {formatDate(client.date_fin_contrat_client)}
                            </p>
                          </div>
                        </div>
                        
                        {/* Afficher le message du contrat */}
                        <div className="text-center mt-3">
                          <span className="badge fw-semibold" style={{
                            ...contratStyle,
                            padding: '10px 20px',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            border: `2px solid ${contratStyle.borderColor}`,
                            display: 'inline-block',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            <i className={`bi me-2 ${
                              client.type_jours === 'restant' ? 'bi-clock' : 
                              client.type_jours === 'termine' ? 'bi-clock-history' : 'bi-question-circle'
                            }`}></i>
                            {client.message_contrat || `${client.jours_contrat} jour(s)`}
                          </span>
                          
                          {/* Légende des couleurs */}
                          <div className="mt-3 d-flex justify-content-center flex-wrap gap-2 small">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-1" style={{ 
                                width: '10px', 
                                height: '10px', 
                                backgroundColor: '#4caf50' 
                              }}></div>
                              <span style={{ color: '#6c757d' }}>Plus de 30 jours</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-1" style={{ 
                                width: '10px', 
                                height: '10px', 
                                backgroundColor: '#ff9800' 
                              }}></div>
                              <span style={{ color: '#6c757d' }}>1-30 jours</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-1" style={{ 
                                width: '10px', 
                                height: '10px', 
                                backgroundColor: '#f44336' 
                              }}></div>
                              <span style={{ color: '#6c757d' }}>Dernier jour</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle me-1" style={{ 
                                width: '10px', 
                                height: '10px', 
                                backgroundColor: '#9e9e9e' 
                              }}></div>
                              <span style={{ color: '#6c757d' }}>Expiré</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Sites associés */}
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>
                    <i className="bi bi-buildings me-2" style={{ color: COLORS.secondary }}></i>
                    Sites associés ({client.statistiques?.nb_sites || 0})
                  </span>
                  {isGestionnaire && !isDeleted && (
                    <Link
                      to={`/sites/create?client_id=${client.id_client}`}
                      className="btn btn-sm fw-semibold"
                      style={{
                        color: 'white',
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                        borderRadius: '6px',
                        padding: '6px 12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.secondary;
                        e.target.style.borderColor = COLORS.secondary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.borderColor = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-plus-lg me-1"></i> Ajouter un site
                    </Link>
                  )}
                </h5>
                
                {sites.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ 
                      width: '80px',
                      height: '80px',
                      backgroundColor: COLORS.lightBg,
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-buildings fs-3"></i>
                    </div>
                    <p className="mb-3" style={{ color: COLORS.darkText }}>Aucun site associé à ce client</p>
                    {isGestionnaire && !isDeleted && (
                      <Link
                        to={`/sites/create?client_id=${client.id_client}`}
                        className="btn fw-semibold"
                        style={{
                          color: 'white',
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                          borderRadius: '8px',
                          padding: '10px 20px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.secondary;
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-plus-lg me-1"></i> Ajouter un site
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr style={{ backgroundColor: COLORS.lightBg }}>
                          <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Nom du site</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Adresse</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Statut</th>
                          <th style={{ color: COLORS.primary, fontWeight: '600', border: 'none' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sites.map((site) => (
                          <tr key={site.id_site} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                            <td style={{ color: COLORS.darkText, padding: '16px 8px' }}>{site.nom_site}</td>
                            <td style={{ color: COLORS.darkText, padding: '16px 8px' }}>{site.adresse_site || '-'}</td>
                            <td style={{ padding: '16px 8px' }}>
                              <span className="badge" style={{ 
                                backgroundColor: site.statut_site === 1 ? '#e8f5e9' : '#f5f5f5',
                                color: site.statut_site === 1 ? '#2e7d32' : '#757575',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                fontWeight: '500'
                              }}>
                                {site.statut_site === 1 ? 'Actif' : 'Inactif'}
                              </span>
                            </td>
                            <td style={{ padding: '16px 8px' }}>
                              <Link
                                to={`/sites/details/${site.id_site}`}
                                className="btn btn-sm fw-semibold"
                                style={{
                                  color: COLORS.primary,
                                  borderColor: COLORS.primary,
                                  backgroundColor: 'transparent',
                                  borderRadius: '6px',
                                  padding: '6px 12px',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor = COLORS.primary;
                                  e.target.style.color = 'white';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = 'transparent';
                                  e.target.style.color = COLORS.primary;
                                }}
                              >
                                <i className="bi bi-eye"></i> Voir
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong,
        .table th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .form-select,
        .form-text,
        .alert,
        .table td,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .tooltip-container {
          cursor: pointer;
          position: relative;
          display: inline-block;
        }
        
        .tooltip-container:hover .tooltip-text {
          display: block !important;
        }
        
        .tooltip-container .tooltip-text {
          display: none;
          position: absolute;
          z-index: 1000;
          background-color: #212529;
          color: white;
          padding: 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          white-space: nowrap;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
        }
        
        .tooltip-container .tooltip-arrow {
          position: absolute;
          border-width: 5px;
          border-style: solid;
        }
        
        .btn-icon:hover {
          transform: scale(1.1);
          transition: transform 0.2s;
        }
        
        .btn-icon:active {
          transform: scale(0.95);
        }
        
        .table {
          border-collapse: separate;
          border-spacing: 0;
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .col-lg-4, .col-lg-8 {
            margin-bottom: 1.5rem;
          }
          
          .table-responsive {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
        }
      `}</style>
    </Layout>
  );
};

export default ClientDetails;